import React, { FC, useEffect, useRef, useState } from 'react';
import type {
  OrganisationCardPolicy,
  OrganisationCardPolicyOverride,
  OrganisationCardLimitMember,
  SetOrganisationCardPolicyOverrideRequest,
} from '@dreamai/types';
import { ReactComponent as ArrowLeftIcon } from '../../icons/arrow-left.svg';
import { capLabel, parseOptionalMoney } from './organisationCardLabels';
import OrganisationToggle from './OrganisationToggle';
import './delete-account-modal.scss';

/** One member's pending edit: back to the organisation limits, or own caps. */
export type MemberLimitChange =
  | { member: OrganisationCardLimitMember; kind: 'inherit' }
  | {
      member: OrganisationCardLimitMember;
      kind: 'set';
      values: SetOrganisationCardPolicyOverrideRequest;
    };

interface OrganisationCardMemberLimitsModalProps {
  policy: OrganisationCardPolicy;
  overrides: OrganisationCardPolicyOverride[];
  personalAssistantMembers: OrganisationCardLimitMember[];
  teamAssistantUsers: OrganisationCardLimitMember[];
  busy: boolean;
  onClose: () => void;
  /** Applies every pending change; resolves true when all were saved. */
  onSave: (changes: MemberLimitChange[]) => Promise<boolean>;
}

interface Draft {
  same: boolean;
  cardEnabled: boolean | null;
  perTransactionCap: string;
  cycleCap: string;
}

function draftFor(
  member: OrganisationCardLimitMember,
  override: OrganisationCardPolicyOverride | undefined,
): Draft {
  return {
    same: !override,
    cardEnabled: override?.cardEnabled ?? null,
    perTransactionCap:
      override?.perTransactionCap == null
        ? ''
        : String(override.perTransactionCap),
    cycleCap: override?.cycleCap == null ? '' : String(override.cycleCap),
  };
}

function describeOverride(
  override: OrganisationCardPolicyOverride | undefined,
  currency: string,
): string {
  if (!override) return 'Same as organisation';
  if (override.cardEnabled === false) return 'Purchases off';
  const parts: string[] = [];
  if (override.perTransactionCap !== null) {
    parts.push(
      `${capLabel(override.perTransactionCap, currency)} per purchase`,
    );
  }
  if (override.cycleCap !== null) {
    parts.push(`${capLabel(override.cycleCap, currency)} per month`);
  }
  if (parts.length === 0) {
    return override.cardEnabled === true
      ? 'Purchases on'
      : 'Same as organisation';
  }
  return parts.join(' · ');
}

function describeDraft(draft: Draft, currency: string): string {
  if (draft.same) return 'Same as organisation';
  if (draft.cardEnabled === false) return 'Purchases off';
  const per = parseOptionalMoney(draft.perTransactionCap);
  const cycle = parseOptionalMoney(draft.cycleCap);
  const parts: string[] = [];
  if (per != null) parts.push(`${capLabel(per, currency)} per purchase`);
  if (cycle != null) parts.push(`${capLabel(cycle, currency)} per month`);
  return parts.length ? parts.join(' · ') : 'Own limits';
}

/**
 * "Adjust spending limits by member": every active seat with its limits.
 * Edits are kept locally until Save, which writes each changed member
 * (PUT for own caps, DELETE for "Same as my organisation limits" when an
 * override existed); Cancel discards them all. Blank caps fall back to the
 * organisation's, which is why the inputs show those as placeholders.
 */
const OrganisationCardMemberLimitsModal: FC<
  OrganisationCardMemberLimitsModalProps
> = ({
  policy,
  overrides,
  personalAssistantMembers,
  teamAssistantUsers,
  busy,
  onClose,
  onSave,
}) => {
  const members = [...personalAssistantMembers, ...teamAssistantUsers];
  const byMember = new Map(
    overrides.filter((o) => o.membershipId).map((o) => [o.membershipId!, o]),
  );
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [editing, setEditing] = useState<OrganisationCardLimitMember | null>(
    null,
  );
  const [expanded, setExpanded] = useState({
    personalAssistant: true,
    teamAssistant: true,
  });
  const [error, setError] = useState<string | null>(null);
  const currency = policy.currency;

  const draftOf = (member: OrganisationCardLimitMember): Draft =>
    drafts[member.id] ??
    draftFor(
      member,
      member.membershipId ? byMember.get(member.membershipId) : undefined,
    );

  const updateDraft = (
    member: OrganisationCardLimitMember,
    patch: Partial<Draft>,
  ) =>
    setDrafts((prev) => ({
      ...prev,
      [member.id]: { ...draftOf(member), ...patch },
    }));

  const handleClose = () => {
    if (!busy) onClose();
  };

  const latest = useRef({ busy, onClose });
  latest.current = { busy, onClose };
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || latest.current.busy) return;
      e.stopPropagation();
      latest.current.onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  const handleSave = async () => {
    const changes: MemberLimitChange[] = [];
    for (const member of members) {
      const draft = drafts[member.id];
      if (!draft) continue;
      const existing = member.membershipId
        ? byMember.get(member.membershipId)
        : undefined;
      if (draft.same) {
        if (existing) changes.push({ member, kind: 'inherit' });
        continue;
      }
      const perTransactionCap = parseOptionalMoney(draft.perTransactionCap);
      const cycleCap = parseOptionalMoney(draft.cycleCap);
      if (perTransactionCap === undefined || cycleCap === undefined) {
        setEditing(member);
        setError('Limits must be amounts of zero or more, or left blank.');
        return;
      }
      const values: SetOrganisationCardPolicyOverrideRequest = {
        cardEnabled: draft.cardEnabled,
        perTransactionCap,
        cycleCap,
      };
      const unchanged =
        existing &&
        existing.cardEnabled === values.cardEnabled &&
        existing.perTransactionCap === perTransactionCap &&
        existing.cycleCap === cycleCap;
      if (unchanged) continue;
      const empty =
        values.cardEnabled === null &&
        perTransactionCap === null &&
        cycleCap === null;
      if (empty) {
        if (existing) changes.push({ member, kind: 'inherit' });
        continue;
      }
      changes.push({ member, kind: 'set', values });
    }
    setError(null);
    if (changes.length === 0) {
      onClose();
      return;
    }
    if (await onSave(changes)) onClose();
  };

  const dirty = Object.keys(drafts).length > 0;
  const renderMemberSection = (
    title: 'Personal Assistant members' | 'Team Assistant users',
    section: 'personalAssistant' | 'teamAssistant',
    sectionMembers: OrganisationCardLimitMember[],
  ) => (
    <section className='organisation-member-limits-section'>
      <button
        type='button'
        className='organisation-member-limits-section-title'
        aria-expanded={expanded[section]}
        onClick={() =>
          setExpanded((previous) => ({
            ...previous,
            [section]: !previous[section],
          }))
        }
      >
        {title}
      </button>
      {expanded[section] &&
        (sectionMembers.length === 0 ? (
          <p className='organisation-empty'>No members yet.</p>
        ) : (
          <ul className='organisation-member-limits-list'>
            {sectionMembers.map((member) => {
              const draft = drafts[member.id];
              const existing = member.membershipId
                ? byMember.get(member.membershipId)
                : undefined;
              return (
                <li
                  key={member.id}
                  className='organisation-member-limits-item'
                  data-testid={`member-limits-${member.id}`}
                >
                  <span className='organisation-member-limits-name'>
                    {member.name}
                  </span>
                  <span className='organisation-member-limits-summary'>
                    {draft
                      ? describeDraft(draft, currency)
                      : existing
                        ? describeOverride(existing, currency)
                        : describeDraft(
                            {
                              same: true,
                              cardEnabled: member.effectiveLimits.cardEnabled,
                              perTransactionCap: '',
                              cycleCap: '',
                            },
                            currency,
                          )}
                  </span>
                  <button
                    type='button'
                    className='btn-secondary'
                    disabled={busy}
                    onClick={() => {
                      setError(null);
                      setEditing(member);
                    }}
                  >
                    Change
                  </button>
                </li>
              );
            })}
          </ul>
        ))}
    </section>
  );

  return (
    <div
      className='delete-account-modal-overlay'
      onClick={handleClose}
      data-testid='organisation-member-limits-modal'
    >
      <div
        className='delete-account-modal organisation-member-limits'
        role='dialog'
        aria-modal='true'
        aria-labelledby='organisation-member-limits-title'
        onClick={(e) => e.stopPropagation()}
      >
        <div className='modal-header'>
          <p className='modal-title' id='organisation-member-limits-title'>
            Adjust spending limits by member
          </p>
          <button
            type='button'
            className='close-button'
            onClick={handleClose}
            aria-label='Close'
            disabled={busy}
          >
            <svg
              width='20'
              height='20'
              viewBox='0 0 24 24'
              fill='none'
              stroke='#374562'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
            >
              <line x1='18' y1='6' x2='6' y2='18' />
              <line x1='6' y1='6' x2='18' y2='18' />
            </svg>
          </button>
        </div>

        <div className='modal-body'>
          {editing === null ? (
            <>
              {renderMemberSection(
                'Personal Assistant members',
                'personalAssistant',
                personalAssistantMembers,
              )}
              {renderMemberSection(
                'Team Assistant users',
                'teamAssistant',
                teamAssistantUsers,
              )}
            </>
          ) : (
            <>
              <button
                type='button'
                className='btn-link organisation-member-limits-back'
                disabled={busy}
                onClick={() => {
                  setError(null);
                  setEditing(null);
                }}
              >
                <ArrowLeftIcon aria-hidden='true' />
                Back to member list
              </button>
              <div
                className='organisation-member-limits-detail'
                data-testid={`member-limits-detail-${editing.id}`}
              >
                <h3 className='organisation-member-limits-heading'>
                  Adjust limit for {editing.name}
                </h3>
                <div className='organisation-team-row'>
                  <span className='organisation-team-row-label'>
                    Same as my organisation limits
                  </span>
                  <OrganisationToggle
                    on={draftOf(editing).same}
                    disabled={busy}
                    label='Same as my organisation limits'
                    onChange={(same) => updateDraft(editing, { same })}
                  />
                </div>
                {!draftOf(editing).same && (
                  <>
                    <div className='organisation-team-row'>
                      <span className='organisation-team-row-label'>
                        Allow purchases with the organisation card
                      </span>
                      <OrganisationToggle
                        on={
                          draftOf(editing).cardEnabled ??
                          editing.effectiveLimits.cardEnabled
                        }
                        disabled={busy}
                        label='Allow purchases with the organisation card'
                        onChange={(cardEnabled) =>
                          updateDraft(editing, { cardEnabled })
                        }
                      />
                    </div>
                    <label className='organisation-team-row'>
                      <span className='organisation-team-row-label'>
                        Per purchase
                      </span>
                      <input
                        type='number'
                        className='field-input'
                        min={0}
                        step='0.01'
                        placeholder={capLabel(
                          editing.effectiveLimits.perTransactionCap,
                          currency,
                        )}
                        value={draftOf(editing).perTransactionCap}
                        disabled={busy}
                        onChange={(e) =>
                          updateDraft(editing, {
                            perTransactionCap: e.target.value,
                          })
                        }
                      />
                    </label>
                    <label className='organisation-team-row'>
                      <span className='organisation-team-row-label'>
                        Per month
                      </span>
                      <input
                        type='number'
                        className='field-input'
                        min={0}
                        step='0.01'
                        placeholder={capLabel(
                          editing.effectiveLimits.cycleCap,
                          currency,
                        )}
                        value={draftOf(editing).cycleCap}
                        disabled={busy}
                        onChange={(e) =>
                          updateDraft(editing, { cycleCap: e.target.value })
                        }
                      />
                    </label>
                  </>
                )}
              </div>
            </>
          )}
          {error && (
            <p className='organisation-feedback error' role='alert'>
              {error}
            </p>
          )}
        </div>

        <div className='modal-footer'>
          <button
            type='button'
            className='btn-delete-dismiss'
            onClick={handleClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type='button'
            className='btn-primary'
            onClick={handleSave}
            disabled={busy || !dirty}
          >
            {busy ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrganisationCardMemberLimitsModal;
